import { Injectable } from '@angular/core';
import {HttpClient, HttpParams} from '@angular/common/http';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';

import {
  AccessSession,
  PracticeAccessData,
  GoogleSheetsRow,
  AccessDetail,
  AgenzieResponse, AuditResponse, AuditFilters
} from './models/access-monitoring.model';

@Injectable({
  providedIn: 'root'
})
export class AccessMonitoringService {
  private practiceData = new Map<string, PracticeAccessData>();
  private currentSession: AccessSession | null = null;
  private currentPracticeId: string | null = null;
  private lastActivity: number = Date.now();
  private readonly INACTIVITY_TIMEOUT = 30 * 60 * 1000; // 30 minuti
  private readonly HEARTBEAT_INTERVAL = 30000; // 30 secondi
  private readonly SESSION_KEY = 'currentAccessSession';
  private readonly DATA_KEY = 'practiceAccessData';
  private readonly GOOGLE_SHEETS_URL = 'https://script.google.com/macros/s/AKfycbylfmWa2RCbxTvDRNIuC3QfckOHiseI-jcKJQ8ft0wm7MMpKLWglfH7VuTa8WMfix30/exec';
  private sessionDataSent = false;

  constructor(private http: HttpClient) {
    this.setupActivityTracking();
    this.startHeartbeat();
    this.recoverSession();
    this.setupUnloadHandlers();
  }

  /**
   * Configura il tracking dell'attività utente
   */
  private setupActivityTracking(): void {
    const events = ['mousedown', 'mousemove', 'keypress', 'scroll', 'touchstart', 'click'];

    events.forEach(event => {
      document.addEventListener(event, () => {
        this.updateLastActivity();
      }, true);
    });

    // Controlla inattività ogni minuto
    setInterval(() => {
      this.checkInactivity();
    }, 60000);
  }

  /**
   * Configura gli handler per la chiusura browser
   */
  private setupUnloadHandlers(): void {
    window.addEventListener('beforeunload', () => {
      this.handleBeforeUnload();
    });

    window.addEventListener('unload', () => {
      this.handleUnload();
    });
  }

  /**
   * Aggiorna timestamp ultima attività
   */
  private updateLastActivity(): void {
    this.lastActivity = Date.now();

    if (this.currentSession) {
      console.log('👆 Attività rilevata - sessione ancora attiva');
    }
  }

  /**
   * Controlla se l'utente è inattivo da troppo tempo
   */
  private checkInactivity(): void {
    const now = Date.now();
    const inactiveTime = now - this.lastActivity;

    if (inactiveTime > this.INACTIVITY_TIMEOUT && this.currentSession && this.currentPracticeId) {
      console.log(`😴 Sessione terminata per inattività (${inactiveTime/1000/60} minuti)`);
      this.endSessionDueToInactivity();
    }
  }

  /**
   * Inizia una nuova sessione di accesso
   */
  startAccessSession(numeroPratica: string, importoTotale: number, agenzia: string, cognomeIntestatario: string): void {
    // Se è la stessa pratica, ESTENDI la sessione esistente
    if (this.currentSession && this.currentPracticeId === numeroPratica) {
      console.log(`🔄 Estendendo sessione esistente per ${numeroPratica}`);
      this.updatePracticeInfo(numeroPratica, importoTotale, agenzia, cognomeIntestatario);
      this.updateLastActivity();
      return;
    }

    // Se è una pratica diversa, termina la precedente
    if (this.currentSession && this.currentPracticeId && this.currentPracticeId !== numeroPratica) {
      this.endAccessSession(this.currentPracticeId);
    }

    // Inizia NUOVA sessione solo per pratica diversa
    const now = new Date();
    this.currentSession = {
      sessionId: this.generateSessionId(),
      startTime: now
    };

    this.currentPracticeId = numeroPratica;
    this.sessionDataSent = false;
    this.lastActivity = now.getTime();

    this.initializePracticeData(numeroPratica, importoTotale, agenzia, cognomeIntestatario);
    this.saveCurrentSession();

    console.log(`✅ NUOVA sessione iniziata per ${numeroPratica}`);
  }

  /**
   * Termina una sessione di accesso
   */
  endAccessSession(numeroPratica: string): void {
    if (!this.currentSession || this.currentPracticeId !== numeroPratica) {
      return;
    }

    const now = new Date();
    this.currentSession.endTime = now;
    this.currentSession.duration = this.calculateDurationMs(this.currentSession.startTime, now);

    this.updatePracticeData(numeroPratica, this.currentSession, true);
    this.saveDataLocally();
    this.sendToGoogleSheets(numeroPratica);

    console.log(`✅ Sessione terminata correttamente per ${numeroPratica}: ${this.currentSession.duration/1000/60} minuti`);
    this.clearCurrentSession();
  }

  /**
   * Heartbeat per mantenere lo stato
   */
  private startHeartbeat(): void {
    setInterval(() => {
      if (this.currentSession && this.currentPracticeId) {
        this.saveCurrentSession();
        console.log(`💓 Heartbeat - sessione attiva da ${this.getSessionDurationMinutes()} minuti`);
      }
    }, this.HEARTBEAT_INTERVAL);
  }

  /**
   * Termina sessione per inattività
   */
  private endSessionDueToInactivity(): void {
    if (!this.currentSession || !this.currentPracticeId) return;

    const endTime = new Date(this.lastActivity);
    this.currentSession.endTime = endTime;
    this.currentSession.duration = this.calculateDurationMs(this.currentSession.startTime, endTime);

    this.updatePracticeData(this.currentPracticeId, this.currentSession, false);
    this.saveDataLocally();
    this.sendToGoogleSheets(this.currentPracticeId);

    console.log(`⏰ Sessione terminata per timeout dopo ${this.currentSession.duration/1000/60} minuti`);
    this.clearCurrentSession();
  }

  private handleBeforeUnload(): void {
    console.log('🔍 BEFOREUNLOAD - Inizio debug');
    console.log('🔍 currentSession:', this.currentSession);
    console.log('🔍 currentPracticeId:', this.currentPracticeId);
    console.log('🔍 sessionDataSent:', this.sessionDataSent);

    if (this.currentSession && this.currentPracticeId && !this.sessionDataSent) {
      console.log('🔍 CONDIZIONI SODDISFATTE - Procedendo con invio');

      const now = new Date();
      const sessionCopy = {
        ...this.currentSession,
        endTime: now,
        duration: this.calculateDurationMs(this.currentSession.startTime, now)
      };

      console.log('🔍 sessionCopy creata:', sessionCopy);

      this.updatePracticeData(this.currentPracticeId, sessionCopy, true);

      const practiceData = this.practiceData.get(this.currentPracticeId);
      console.log('🔍 practiceData:', practiceData);

      if (practiceData && practiceData.dettagliAccesso.length > 0) {
        console.log('🔍 Dati trovati, creando riga...');

        const latestAccess = practiceData.dettagliAccesso[practiceData.dettagliAccesso.length - 1];
        console.log('🔍 latestAccess:', latestAccess);

        const row = this.createGoogleSheetsRow(practiceData, latestAccess);
        console.log('🔍 Riga creata:', row);
        console.log('🔍 URL destinazione:', this.GOOGLE_SHEETS_URL);

        // Test fetch prima
        if ('fetch' in window) {
          console.log('🔍 Tentando con fetch keepalive...');

          fetch(this.GOOGLE_SHEETS_URL, {
            method: 'POST',
            headers: {
              // 'Content-Type': 'application/json',
              'Content-Type': "text/plain;charset=utf-8"
            },
            body: JSON.stringify({ data: [row] }),
            keepalive: true
          }).then(response => {
            console.log('✅ Fetch keepalive riuscita:', response.status);
          }).catch(error => {
            console.error('❌ Fetch keepalive fallita:', error);

            // Fallback a sendBeacon
            if (navigator.sendBeacon) {
              console.log('🔍 Tentando fallback con sendBeacon...');
              const success = navigator.sendBeacon(this.GOOGLE_SHEETS_URL, JSON.stringify({ data: [row] }));
              console.log('📤 SendBeacon result:', success);
            } else {
              console.log('❌ SendBeacon non supportato');
            }
          });
        } else if (navigator.sendBeacon) {
          console.log('🔍 Fetch non disponibile, usando sendBeacon...');
          const success = navigator.sendBeacon(this.GOOGLE_SHEETS_URL, JSON.stringify({ data: [row] }));
          console.log('📤 SendBeacon result:', success);
        } else {
          console.log('❌ Nessun metodo di invio disponibile');
        }
      } else {
        console.log('❌ Nessun dato pratica o dettagli accesso');
        console.log('- practiceData exists:', !!practiceData);
        console.log('- dettagliAccesso length:', practiceData?.dettagliAccesso?.length || 0);
      }

      this.sessionDataSent = true;
      console.log(`🚪 Sessione chiusa correttamente alla chiusura browser`);
    } else {
      console.log('❌ CONDIZIONI NON SODDISFATTE');
      console.log('- currentSession exists:', !!this.currentSession);
      console.log('- currentPracticeId exists:', !!this.currentPracticeId);
      console.log('- sessionDataSent:', this.sessionDataSent);
    }
  }

  /**
   * Gestisce evento unload
   */
  private handleUnload(): void {
    if (this.currentSession && this.currentPracticeId) {
      this.saveCurrentSession();
    }
  }

  /**
   * Recupera sessioni interrotte
   */
  private recoverSession(): void {
    const storedSession = localStorage.getItem(this.SESSION_KEY);
    if (storedSession) {
      try {
        const sessionData = JSON.parse(storedSession);
        const session = sessionData.session;
        const practiceId = sessionData.practiceId;

        const recoveredSession: AccessSession = {
          ...session,
          startTime: new Date(session.startTime),
          endTime: new Date(sessionData.timestamp),
          duration: new Date(sessionData.timestamp).getTime() - new Date(session.startTime).getTime()
        };

        this.updatePracticeData(practiceId, recoveredSession, false);
        this.saveDataLocally();
        this.sendToGoogleSheets(practiceId);

        console.log(`🔄 Sessione precedente recuperata: ${recoveredSession.duration!/1000/60} minuti`);
        localStorage.removeItem(this.SESSION_KEY);
      } catch (error) {
        console.error('❌ Errore nel recupero della sessione:', error);
        localStorage.removeItem(this.SESSION_KEY);
      }
    }
  }

  /**
   * Inizializza i dati per una nuova pratica
   */
  private initializePracticeData(numeroPratica: string, importoTotale: number, agenzia: string, cognomeIntestatario: string): void {
    if (!this.practiceData.has(numeroPratica)) {
      this.practiceData.set(numeroPratica, {
        numeroPratica,
        importoTotale,
        agenzia,
        cognomeIntestatario,
        ultimaVisita: new Date(),
        dettagliAccesso: []
      });
    } else {
      // Aggiorna ultima visita
      const data = this.practiceData.get(numeroPratica)!;
      data.ultimaVisita = new Date();
    }
  }

  /**
   * Aggiorna i dati della pratica con la sessione
   */
  private updatePracticeData(numeroPratica: string, session: AccessSession, sessioneConclusaCorrettamente: boolean): void {
    const data = this.practiceData.get(numeroPratica);
    if (!data) return;

    data.dettagliAccesso.push({
      dataAccesso: session.startTime,
      oraInizioSessione: session.startTime,
      oraFineSessione: session.endTime,
      durataSessione: session.duration,
      sessioneConclusaCorrettamente
    });
  }

  /**
   * Aggiorna info pratica senza creare nuova sessione
   */
  private updatePracticeInfo(numeroPratica: string, importoTotale: number, agenzia: string, cognomeIntestatario: string): void {
    const data = this.practiceData.get(numeroPratica);
    if (data) {
      data.importoTotale = importoTotale;
      data.agenzia = agenzia;
      data.cognomeIntestatario = cognomeIntestatario;
      data.ultimaVisita = new Date();
    }
  }

  /**
   * Invia dati a Google Sheets
   */
  private sendToGoogleSheets(numeroPratica: string): void {
    const practiceData = this.practiceData.get(numeroPratica);
    if (!practiceData || practiceData.dettagliAccesso.length === 0) return;

    const latestAccess = practiceData.dettagliAccesso[practiceData.dettagliAccesso.length - 1];
    const row = this.createGoogleSheetsRow(practiceData, latestAccess);

    this.sendDataToGoogleSheets([row]).subscribe({
      next: (response) => {
        console.log('✅ Dati inviati con successo:', response);
      },
      error: (error) => {
        console.error('❌ Errore nell\'invio dati:', error);
        this.saveFailedDataForRetry([row]);
      }
    });
  }

  /**
   * Effettua la chiamata HTTP a Google Sheets
   */
  private sendDataToGoogleSheets(data: GoogleSheetsRow[]): Observable<any> {
    const headers = {
      'Content-Type': 'application/json'
    };

    return this.http.post(this.GOOGLE_SHEETS_URL, { data }, { headers });
  }

  /**
   * Salva dati falliti per retry
   */
  private saveFailedDataForRetry(data: GoogleSheetsRow[]): void {
    try {
      const failedDataKey = 'failedAccessData';
      const existingData = localStorage.getItem(failedDataKey);
      let failedQueue: GoogleSheetsRow[] = [];

      if (existingData) {
        failedQueue = JSON.parse(existingData);
      }

      // Aggiungi i nuovi dati falliti
      failedQueue.push(...data);

      // Mantieni solo gli ultimi 100 record per evitare overflow
      if (failedQueue.length > 100) {
        failedQueue = failedQueue.slice(-100);
      }

      localStorage.setItem(failedDataKey, JSON.stringify(failedQueue));
      console.warn('🔄 Dati salvati per retry futuro:', data.length, 'record');

      // Programma un retry dopo 5 minuti
      setTimeout(() => {
        this.retryFailedData();
      }, 5 * 60 * 1000);

    } catch (error) {
      console.error('❌ Errore nel salvare dati per retry:', error);
    }
  }

  /**
   * Riprova ad inviare i dati falliti
   */
  private retryFailedData(): void {
    const failedDataKey = 'failedAccessData';
    const failedData = localStorage.getItem(failedDataKey);

    if (!failedData) return;

    try {
      const dataToRetry: GoogleSheetsRow[] = JSON.parse(failedData);

      if (dataToRetry.length === 0) return;

      console.log(`🔄 Tentativo di retry per ${dataToRetry.length} record falliti`);

      this.sendDataToGoogleSheets(dataToRetry).subscribe({
        next: (response) => {
          console.log('✅ Retry completato con successo:', response);
          localStorage.removeItem(failedDataKey);
        },
        error: (error) => {
          console.error('❌ Retry fallito, i dati rimangono in coda:', error);
          // I dati rimangono in localStorage per un altro tentativo futuro
        }
      });

    } catch (error) {
      console.error('❌ Errore nel parsing dei dati falliti:', error);
      localStorage.removeItem(failedDataKey);
    }
  }

  /**
   * Crea riga per Google Sheets
   */
  private createGoogleSheetsRow(practiceData: PracticeAccessData, latestAccess: AccessDetail): GoogleSheetsRow {
    return {
      'Numero Pratica': practiceData.numeroPratica,
      'Importo Totale': practiceData.importoTotale,
      'Agenzia': practiceData.agenzia,
      'Cognome Intestatario': practiceData.cognomeIntestatario,
      'Ultima Visita': practiceData.ultimaVisita.toISOString(),
      'Data Accesso': latestAccess.dataAccesso.toISOString().split('T')[0],
      'Ora Inizio Sessione': latestAccess.oraInizioSessione.toISOString(),
      'Ora Fine Sessione': latestAccess.oraFineSessione?.toISOString() || '',
      'Durata Sessione (ms)': latestAccess.durataSessione || 0,
      'Sessione Conclusa': latestAccess.sessioneConclusaCorrettamente
    };
  }

  /**
   * Salva sessione corrente nel localStorage
   */
  private saveCurrentSession(): void {
    if (this.currentSession && this.currentPracticeId) {
      const sessionData = {
        session: this.currentSession,
        practiceId: this.currentPracticeId,
        timestamp: new Date().toISOString()
      };
      localStorage.setItem(this.SESSION_KEY, JSON.stringify(sessionData));
    }
  }

  /**
   * Salva dati localmente
   */
  private saveDataLocally(): void {
    const dataToSave = Array.from(this.practiceData.entries()).map(([key, value]) => [key, value]);
    localStorage.setItem(this.DATA_KEY, JSON.stringify(dataToSave));
  }

  /**
   * Pulisce la sessione corrente
   */
  private clearCurrentSession(): void {
    this.currentSession = null;
    this.currentPracticeId = null;
    this.sessionDataSent = false;
    localStorage.removeItem(this.SESSION_KEY);
  }

  /**
   * Genera ID sessione univoco
   */
  private generateSessionId(): string {
    return Date.now().toString(36) + Math.random().toString(36).substr(2);
  }

  /**
   * Calcola durata in millisecondi
   */
  private calculateDurationMs(startTime: Date, endTime: Date): number {
    return endTime.getTime() - startTime.getTime();
  }

  /**
   * Ottiene durata sessione corrente in minuti
   */
  private getSessionDurationMinutes(): number {
    if (!this.currentSession) return 0;
    return Math.floor((Date.now() - this.currentSession.startTime.getTime()) / 60000);
  }

  /**
   * Metodo pubblico per aggiornare dati sessione (se necessario)
   */
  updateSessionData(numeroPratica: string, importoTotale: number, agenzia: string, cognomeIntestatario: string): void {
    if (this.currentPracticeId === numeroPratica) {
      this.updatePracticeInfo(numeroPratica, importoTotale, agenzia, cognomeIntestatario);
      this.saveCurrentSession();
    }
  }

  /**
   * Metodo pubblico per ottenere durata sessione corrente
   */
  getCurrentSessionDuration(): number {
    return this.getSessionDurationMinutes();
  }

  /**
   * Metodo pubblico per verificare se c'è una sessione attiva
   */
  hasActiveSession(): boolean {
    return this.currentSession !== null;
  }

  /**
   * Recupera i dati di audit con filtri
   */
  getAuditData(filters: AuditFilters): Observable<AuditResponse> {
    let params = new HttpParams();
    params = params.append('action', 'getAuditData');
    // Serializza i filtri come parametri di query
    Object.entries(filters).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        params = params.append(key, value);
      }
    });
    return this.http.get<AuditResponse>(this.GOOGLE_SHEETS_URL, { params }).pipe(
      map(response => {
        // Converti le date stringa in oggetti Date
        if (response.success && response.data) {
          response.data.forEach(item => {
            item.ultimaVisita = new Date(item.ultimaVisita);
            item.sessioniDettagliate.forEach(sessione => {
              sessione.dataAccesso = new Date(sessione.dataAccesso);
            });
          });
        }
        return response;
      })
    );
  }

  /**
   * Recupera lista agenzie per autocomplete
   */
  getAgenzie(): Observable<AgenzieResponse> {
    let params = new HttpParams();
    params = params.append('action', 'getAgenzie');
    return this.http.get<AgenzieResponse>(this.GOOGLE_SHEETS_URL, { params });
  }

}
